// reducer
import * as Dialog from '@radix-ui/react-dialog';
import { useReducer } from 'react';
import { Chain } from 'viem';

import {
  ADVANCED_CLOSED_ORDER_STATUS_FILTER_OPTIONS,
  isAdvancedClosedOrderStatus,
} from '@/constants/advancedOrder';

import { dialogCloseBtnStyles } from '@/styles/dialog';

import { ChainAvatar } from '@/ui/Avatars/ChainAvatar';
import { Checkbox } from '@/ui/Checkbox';
import { XIcon } from '@/ui/Icons/XIcon';
import { Radio } from '@/ui/Radio';
import { Select } from '@/ui/Select';

import { buttons } from '../../../../styles/buttons';
import { fontStyles } from '../../../../styles/typography';
import {
  timeFilters,
  filtersReducer,
  FILTER_ACTIONS,
  DEFAULT_FILTERS_STATE,
  type FiltersReducerState,
} from './reducer';

interface MobileProps {
  networks: Chain[];
  defaultValues?: FiltersReducerState;
  onSave: (filterState: FiltersReducerState) => void;
  onClose: () => void;
  showStatus?: boolean;
}

export const Mobile = ({
  networks,
  onClose,
  onSave,
  defaultValues,
  showStatus = false,
}: MobileProps) => {
  const [uncontrolledState, dispatch] = useReducer(
    filtersReducer,
    defaultValues ?? DEFAULT_FILTERS_STATE,
  );
  const disabled =
    uncontrolledState.time === 0 &&
    !uncontrolledState.orderTypes.length &&
    !uncontrolledState.networks.length &&
    !uncontrolledState.status;

  return (
    <div className="flex h-full flex-col">
      <div className="p-6">
        <Dialog.Close className={dialogCloseBtnStyles({ absolute: true }).button()}>
          <XIcon aria-hidden className={dialogCloseBtnStyles().icon()} />
        </Dialog.Close>
        <Dialog.Title
          className={fontStyles({ variant: 'LABEL_MD', class: ['text-center', 'mt-1'] })}
        >
          Filters
        </Dialog.Title>
        <fieldset>
          <legend className={fontStyles({ variant: 'LABEL_LG', class: ['py-3'] })}>Network</legend>

          {networks.map((chain) => {
            return (
              <div key={chain.id} className="flex items-center text-350">
                <Checkbox
                  id={chain.name}
                  checked={
                    uncontrolledState.networks.includes(chain.id.toString())
                      ? 'checked'
                      : 'unchecked'
                  }
                  onCheckedChange={() => {
                    const chainId = chain.id.toString();
                    const payload = uncontrolledState.networks.includes(chainId)
                      ? uncontrolledState.networks.filter((network) => network !== chainId)
                      : [...uncontrolledState.networks, chainId];
                    dispatch({ type: FILTER_ACTIONS.UPDATE_NETWORKS, payload });
                  }}
                />
                <ChainAvatar
                  chainId={chain.id}
                  alt={chain.name}
                  size={16}
                  className="ml-3 mr-1.5"
                />
                <label htmlFor={chain.name} className="py-4 w-full">
                  {chain.name}
                </label>
              </div>
            );
          })}
        </fieldset>
        {showStatus && (
          <>
            <hr className="border-0 border-b-1 border-special-border-divider my-4" />
            <fieldset>
              <legend className={fontStyles({ variant: 'LABEL_LG', class: ['py-3'] })}>
                Status
              </legend>
              <Select
                ariaLabel="Advanced order status"
                variant="secondary"
                items={ADVANCED_CLOSED_ORDER_STATUS_FILTER_OPTIONS}
                placeholderText="All statuses"
                selected={uncontrolledState.status ?? 'all'}
                onSelect={(status) =>
                  dispatch({
                    type: FILTER_ACTIONS.UPDATE_STATUS,
                    payload: isAdvancedClosedOrderStatus(status) ? status : undefined,
                  })
                }
                align="start"
                className="w-full justify-between"
              />
            </fieldset>
          </>
        )}
        <hr className="border-0 border-b-1 border-special-border-divider my-4" />
        <fieldset>
          <legend className={fontStyles({ variant: 'LABEL_LG', class: ['py-3'] })}>Time</legend>
          {timeFilters.map((time) => (
            <div key={time.value} className="flex items-center text-350">
              <Radio
                name="time"
                id={time.label}
                value={time.value}
                checked={time.value === uncontrolledState.time}
                onChange={(e) => {
                  dispatch({ type: FILTER_ACTIONS.UPDATE_TIME, payload: Number(e.target.value) });
                }}
              />
              <label
                htmlFor={time.label}
                className={`py-4 w-full ml-3 ${
                  time.value === uncontrolledState.time ? 'font-medium' : ''
                }`}
              >
                {time.label}
              </label>
            </div>
          ))}
        </fieldset>
      </div>

      <footer className="flex p-6 mt-auto border-t-1 border-special-border-divider justify-between">
        <button
          disabled={disabled}
          className={buttons({ size: 'small', shape: 'rounded', color: 'transparent' })}
          onClick={() => {
            dispatch({ type: FILTER_ACTIONS.CLEAR });
          }}
        >
          Clear all
        </button>
        <button
          className={buttons({ size: 'small', shape: 'rounded', color: 'purple' })}
          onClick={() => {
            onSave(uncontrolledState);
            onClose();
          }}
        >
          Save
        </button>
      </footer>
    </div>
  );
};
